import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { useCrud } from '../lib/useCrud';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { 
  Plus, Search, Edit, Trash2, Eye, Building2, Mail, Phone, 
  MapPin, Globe, MoreVertical, Users, Loader2 
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useConfirmation } from '../components/ConfirmationProvider';
import { useAuth } from '../context/AuthContext';

const statusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'prospect', label: 'Prospect' },
];

const sourceOptions = [
  { value: 'referral', label: 'Referral' },
  { value: 'website', label: 'Website' },
  { value: 'social_media', label: 'Social Media' },
  { value: 'cold_call', label: 'Cold Call' },
  { value: 'exhibition', label: 'Exhibition' },
  { value: 'other', label: 'Other' },
];

export const ClientsPage = () => {
  const { data: clients, loading, fetchData, create, update, remove } = useCrud('/clients');
  const confirm = useConfirmation();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('clients.create');
  const canEdit = hasPermission('clients.edit');
  const canDelete = hasPermission('clients.delete');
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '', contact_person: '', email: '', phone: '', website: '',
    address: '', city: '', state: '', country: 'India', pincode: '',
    gstin: '', industry: '', source: 'other', status: 'active', notes: '',
    billing_emails: [],
  });

  // Server-side search + status filter. The earlier client-side filter only
  // looked at the 50 clients loaded by useCrud's default page — anything on
  // page 2+ was invisible, which is why "search returns nothing" once the
  // dataset grew past ~50.
  useEffect(() => {
    const params = { limit: 200 };
    if (searchQuery.trim()) params.search = searchQuery.trim();
    if (statusFilter !== 'all') params.status = statusFilter;
    const handle = setTimeout(() => fetchData(params), 250);
    return () => clearTimeout(handle);
  }, [searchQuery, statusFilter, fetchData]);

  // Deep-link support: when ClientDetailPage redirects with `?edit=<id>`,
  // fetch the target client and open the Edit dialog automatically. We
  // strip the param after consuming it so refreshing doesn't re-trigger.
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId) return;
    let cancelled = false;
    api.get(`/clients/${editId}`)
      .then(r => {
        if (cancelled) return;
        const raw = r.data?.data || r.data;
        if (!raw || !raw.id) return;
        // Inline what handleEdit does — but isolated so the immutability
        // analyzer can see the raw object never escapes into shared state.
        setSelectedClient({ id: raw.id, company_name: raw.company_name });
        setFormData({
          company_name: raw.company_name || '',
          contact_person: raw.contact_person || '',
          email: raw.email || '',
          phone: raw.phone || '',
          website: raw.website || '',
          address: raw.address || raw.company_address || '',
          city: raw.city || '',
          state: raw.state || '',
          country: raw.country || 'India',
          pincode: raw.pincode || '',
          gstin: raw.gstin || raw.gst_number || raw.gst || '',
          industry: raw.industry || '',
          source: raw.source || 'other',
          status: raw.status || 'active',
          notes: raw.notes || '',
          billing_emails: Array.isArray(raw.billing_emails) ? [...raw.billing_emails] : [],
        });
        setIsEditing(true);
        setIsDialogOpen(true);
      })
      .catch(() => toast.error('Failed to load client for editing'))
      .finally(() => {
        if (cancelled) return;
        // Strip the consumed param so back/forward doesn't reopen the modal.
        // Build a fresh URLSearchParams to avoid mutating React state.
        const next = new URLSearchParams(searchParams);
        next.delete('edit');
        setSearchParams(next, { replace: true });
      });
    return () => { cancelled = true; };
  }, []);

  const filtered = clients;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing && selectedClient) {
        await update(selectedClient.id, formData);
        toast.success('Client updated');
      } else {
        await create(formData);
        toast.success('Client created');
      }
      setIsDialogOpen(false);
      resetForm();
    } catch { toast.error('Operation failed'); }
  };

  const handleDelete = async (client) => {
    const confirmed = await confirm({ title: 'Delete Client', description: `Are you sure you want to delete "${client.company_name}"? This action cannot be undone.` });
    if (!confirmed) return;
    try {
      await remove(client.id);
      toast.success('Client deleted');
    } catch { toast.error('Delete failed'); }
  };

  const handleEdit = (client) => {
    setSelectedClient(client);
    setFormData({
      company_name: client.company_name || '',
      contact_person: client.contact_person || '',
      email: client.email || '',
      phone: client.phone || '',
      website: client.website || '',
      address: client.address || '',
      city: client.city || '',
      state: client.state || '',
      country: client.country || 'India',
      pincode: client.pincode || '',
      gstin: client.gstin || client.gst_number || client.gst || '',
      industry: client.industry || '',
      source: client.source || 'other',
      status: client.status || 'active',
      notes: client.notes || '',
      billing_emails: client.billing_emails || [],
    });
    setIsEditing(true);
    setIsDialogOpen(true);
  };

  const handleView = (client) => {
    setSelectedClient(client);
    setIsViewDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      company_name: '', contact_person: '', email: '', phone: '', website: '',
      address: '', city: '', state: '', country: 'India', pincode: '',
      gstin: '', industry: '', source: 'other', status: 'active', notes: '',
      billing_emails: [],
    });
    setSelectedClient(null);
    setIsEditing(false);
  };

  const getStatusBadge = (status) => {
    const config = {
      active: { className: 'bg-emerald-500/10 text-emerald-500' },
      inactive: { className: 'bg-gray-500/10 text-gray-500' },
      prospect: { className: 'bg-amber-500/10 text-amber-500' },
    };
    return <Badge className={config[status]?.className || config.active.className}>{status}</Badge>;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Clients</h1>
          <p className="text-muted-foreground">Manage your client database</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          {canCreate && (
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90" data-testid="add-client-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Client
            </Button>
          </DialogTrigger>
          )}
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{isEditing ? 'Edit Client' : 'Add New Client'}</DialogTitle>
              <DialogDescription>
                {isEditing ? 'Update client information' : 'Enter client details to add them to your database'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="company_name">Company Name *</Label>
                  <Input
                    id="company_name"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    placeholder="Enter company name"
                    required
                    data-testid="client-company-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_person">Contact Person</Label>
                  <Input
                    id="contact_person"
                    value={formData.contact_person}
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                    placeholder="Primary contact name"
                    data-testid="client-contact-person"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="client@example.com"
                    data-testid="client-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+91 98765 43210"
                    data-testid="client-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="https://example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Mumbai"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Select value={formData.state || '_none'} onValueChange={(value) => setFormData({ ...formData, state: value === '_none' ? '' : value })}>
                    <SelectTrigger data-testid="client-state">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">Select state</SelectItem>
                      {["Andaman and Nicobar Islands","Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chandigarh","Chhattisgarh","Dadra and Nagar Haveli and Daman and Diu","Delhi","Goa","Gujarat","Haryana","Himachal Pradesh","Jammu and Kashmir","Jharkhand","Karnataka","Kerala","Ladakh","Lakshadweep","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Puducherry","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal"].map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="source">Source</Label>
                  <Select value={formData.source} onValueChange={(value) => setFormData({ ...formData, source: value })}>
                    <SelectTrigger data-testid="client-source">
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent>
                      {sourceOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                    <SelectTrigger data-testid="client-status">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gstin">GST Number</Label>
                  <Input
                    id="gstin"
                    value={formData.gstin}
                    onChange={(e) => setFormData({ ...formData, gstin: e.target.value })}
                    placeholder="e.g. 27AABCT1234A1Z5"
                    data-testid="client-form-gstin"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="industry">Industry</Label>
                  <Input
                    id="industry"
                    value={formData.industry}
                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                    placeholder="e.g., Technology, Retail"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Full address"
                />
              </div>

              {/* Billing Emails */}
              <div className="space-y-2 pt-2 border-t border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Billing Emails</Label>
                    <p className="text-xs text-muted-foreground">Tax invoices and payment receipts will be sent to these addresses.</p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setFormData({ ...formData, billing_emails: [...(formData.billing_emails || []), { email: '', role: 'to', label: '' }] })}
                    data-testid="add-billing-email"
                  >
                    + Add
                  </Button>
                </div>
                {(formData.billing_emails || []).length === 0 ? (
                  <p className="text-xs text-amber-500 italic">No billing emails added — <strong>tax invoices will NOT be emailed</strong> for this client until at least one billing email is added.</p>
                ) : (formData.billing_emails || []).map((be, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                    <Input
                      className="col-span-5"
                      type="email"
                      placeholder="email@client.com"
                      value={be.email}
                      onChange={e => {
                        const arr = [...formData.billing_emails];
                        arr[idx] = { ...arr[idx], email: e.target.value };
                        setFormData({ ...formData, billing_emails: arr });
                      }}
                      data-testid={`billing-email-${idx}`}
                    />
                    <Select
                      value={be.role || 'to'}
                      onValueChange={v => {
                        const arr = [...formData.billing_emails];
                        arr[idx] = { ...arr[idx], role: v };
                        setFormData({ ...formData, billing_emails: arr });
                      }}
                    >
                      <SelectTrigger className="col-span-2" data-testid={`billing-role-${idx}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="to">TO</SelectItem>
                        <SelectItem value="cc">CC</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      className="col-span-4"
                      placeholder="Label (e.g. Accounts)"
                      value={be.label || ''}
                      onChange={e => {
                        const arr = [...formData.billing_emails];
                        arr[idx] = { ...arr[idx], label: e.target.value };
                        setFormData({ ...formData, billing_emails: arr });
                      }}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="col-span-1"
                      onClick={() => {
                        const arr = formData.billing_emails.filter((_, i) => i !== idx);
                        setFormData({ ...formData, billing_emails: arr });
                      }}
                      data-testid={`remove-billing-email-${idx}`}
                    >
                      ✕
                    </Button>
                  </div>
                ))}
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="client-submit-btn">
                  {isEditing ? 'Update Client' : 'Add Client'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search clients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchData()}
                className="pl-10"
                data-testid="client-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {statusOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{clients.length}</p>
                <p className="text-xs text-muted-foreground">Total Clients</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{clients.filter(c => c.status === 'active').length}</p>
                <p className="text-xs text-muted-foreground">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{clients.filter(c => c.status === 'prospect').length}</p>
                <p className="text-xs text-muted-foreground">Prospects</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-gray-500/10 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-gray-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{clients.filter(c => c.status === 'inactive').length}</p>
                <p className="text-xs text-muted-foreground">Inactive</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : clients.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <Building2 className="h-12 w-12 mb-4 opacity-50" />
              <p>No clients found</p>
            </div>
          ) : (
            <Table data-testid="clients-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Projects</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clients.map((client) => (
                  <TableRow key={client.id} data-testid={`client-row-${client.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-sm font-semibold text-primary">
                            {client.company_name.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <Link
                            to={`/clients/${client.id}`}
                            className="font-medium hover:text-primary hover:underline decoration-dotted underline-offset-2 transition-colors"
                            data-testid={`client-name-link-${client.id}`}
                          >
                            {client.company_name}
                          </Link>
                          <p className="text-xs text-muted-foreground">{client.city}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{client.contact_person || '-'}</TableCell>
                    <TableCell>
                      {client.email ? (
                        <a href={`mailto:${client.email}`} className="text-primary hover:underline">
                          {client.email}
                        </a>
                      ) : '-'}
                    </TableCell>
                    <TableCell>{client.phone || '-'}</TableCell>
                    <TableCell>{getStatusBadge(client.status)}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{client.projects_count || 0}</Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`client-actions-${client.id}`}>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleView(client)}>
                            <Eye className="h-4 w-4 mr-2" />
                            View
                          </DropdownMenuItem>
                          {canEdit && (
                          <DropdownMenuItem onClick={() => handleEdit(client)} data-testid={`edit-client-${client.id}`}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          )}
                          {canDelete && (
                          <DropdownMenuItem 
                            onClick={() => handleDelete(client)}
                            className="text-destructive focus:text-destructive"
                            data-testid={`delete-client-${client.id}`}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* View Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Client Details</DialogTitle>
          </DialogHeader>
          {selectedClient && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-2xl font-bold text-primary">
                    {selectedClient.company_name.charAt(0)}
                  </span>
                </div>
                <div>
                  <h3 className="text-xl font-bold">{selectedClient.company_name}</h3>
                  {getStatusBadge(selectedClient.status)}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Users className="h-4 w-4" />
                  <span>{selectedClient.contact_person || 'No contact'}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Mail className="h-4 w-4" />
                  <span>{selectedClient.email || 'No email'}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Phone className="h-4 w-4" />
                  <span>{selectedClient.phone || 'No phone'}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  <span>{[selectedClient.city, selectedClient.state].filter(Boolean).join(', ') || 'No location'}</span>
                </div>
                {selectedClient.website && (
                  <div className="flex items-center gap-2 text-muted-foreground col-span-2">
                    <Globe className="h-4 w-4" />
                    <a href={selectedClient.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      {selectedClient.website}
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
